/**
 * Roles + permissions matrix.
 *
 * Layout: a sidebar listing every role; clicking a role opens an editable
 * permissions grid grouped by resource. The super_admin role is read-only.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/PageHeader";
import { cn } from "@/lib/cn";

export const Route = createFileRoute("/_app/roles/")({ component: RolesPage });

interface Role {
  id: number;
  slug: string;
  name: string;
  description: string | null;
  is_system: number;
  permission_count: number;
  user_count: number;
}
interface Permission {
  id: number;
  resource: string;
  action: string;
  description: string | null;
}

function RolesPage() {
  const { can } = useAuth();
  const [activeRoleId, setActiveRoleId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);

  const { data: rolesData } = useQuery({
    queryKey: ["roles", "list"],
    queryFn: () => api.get<{ items: Role[] }>("/admin/roles"),
  });
  const { data: permsData } = useQuery({
    queryKey: ["permissions"],
    queryFn: () => api.get<{ items: Permission[] }>("/admin/roles/permissions"),
  });

  const roles = rolesData?.items ?? [];
  const perms = permsData?.items ?? [];
  const activeRole = roles.find((r) => r.id === activeRoleId) ?? roles[0] ?? null;
  useEffect(() => {
    if (activeRoleId == null && roles.length) setActiveRoleId(roles[0].id);
  }, [roles, activeRoleId]);

  return (
    <div className="space-y-2">
      <PageHeader
        title="Roles &amp; permissions"
        description="What each role is allowed to do, by resource. Super admin has everything."
        actions={can("roles", "create") && (
          <Button onClick={() => setCreating(true)}><Plus className="h-4 w-4" /> New role</Button>
        )}
      />

      <div className="grid gap-4 lg:grid-cols-[260px_1fr]">
        {/* Roles sidebar */}
        <Card>
          <CardContent className="p-2">
            <ul className="space-y-0.5">
              {roles.map((r) => (
                <li key={r.id}>
                  <button
                    onClick={() => setActiveRoleId(r.id)}
                    className={cn(
                      "flex w-full flex-col items-start rounded-md px-3 py-2 text-left text-sm transition-colors",
                      activeRoleId === r.id ? "bg-secondary" : "hover:bg-secondary/60",
                    )}
                  >
                    <div className="flex w-full items-center justify-between">
                      <span className="font-medium">{r.name}</span>
                      {!!r.is_system && <Badge variant="outline" className="text-[9px]">System</Badge>}
                    </div>
                    <span className="text-xs text-muted-foreground">{r.permission_count} perms · {r.user_count} users</span>
                  </button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Matrix */}
        {activeRole ? (
          <PermissionMatrix role={activeRole} permissions={perms} />
        ) : (
          <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">Pick a role to edit.</CardContent></Card>
        )}
      </div>

      <NewRoleDialog open={creating} onOpenChange={setCreating} />
    </div>
  );
}

function PermissionMatrix({ role, permissions }: { role: Role; permissions: Permission[] }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const isSuperAdmin = role.slug === "super_admin";

  const { data: assigned } = useQuery({
    queryKey: ["roles", role.id, "permissions"],
    queryFn: () => api.get<{ items: Permission[] }>(`/admin/roles/${role.id}/permissions`),
  });

  const [selected, setSelected] = useState<Set<number>>(new Set());
  useEffect(() => {
    setSelected(new Set((assigned?.items ?? []).map((p) => p.id)));
  }, [assigned]);

  const save = useMutation({
    mutationFn: () => api.put(`/admin/roles/${role.id}/permissions`, { permission_ids: Array.from(selected) }),
    onSuccess: () => { toast.success("Permissions saved"); qc.invalidateQueries({ queryKey: ["roles"] }); },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Save failed"),
  });

  // Group by resource
  const byResource = permissions.reduce<Record<string, Permission[]>>((acc, p) => {
    if (!acc[p.resource]) acc[p.resource] = [];
    acc[p.resource].push(p);
    return acc;
  }, {});

  function toggle(id: number) {
    if (isSuperAdmin) return;
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleResource(resource: string, all: boolean) {
    if (isSuperAdmin) return;
    setSelected((s) => {
      const next = new Set(s);
      const ids = byResource[resource].map((p) => p.id);
      ids.forEach((id) => all ? next.add(id) : next.delete(id));
      return next;
    });
  }

  const dirty = JSON.stringify([...selected].sort()) !== JSON.stringify((assigned?.items ?? []).map((p) => p.id).sort());

  return (
    <Card>
      <CardContent className="p-6">
        <div className="mb-4 flex items-end justify-between gap-4">
          <div>
            <h2 className="text-xl font-medium">{role.name}</h2>
            {role.description && <p className="text-sm text-muted-foreground">{role.description}</p>}
            {isSuperAdmin && <p className="mt-2 text-xs text-warning"><Badge variant="warning">System role</Badge> &nbsp;Super admin permissions are immutable.</p>}
          </div>
          <Button
            disabled={!can("roles", "update") || isSuperAdmin || !dirty || save.isPending}
            onClick={() => save.mutate()}
          >
            <Save className="h-4 w-4" /> Save permissions
          </Button>
        </div>

        <div className="space-y-3">
          {Object.entries(byResource).map(([resource, items]) => {
            const allSelected = items.every((p) => selected.has(p.id));
            const someSelected = items.some((p) => selected.has(p.id));
            return (
              <div key={resource} className="rounded-md border">
                <div className="flex items-center justify-between border-b bg-secondary/40 px-4 py-2">
                  <span className="font-medium capitalize">{resource.replace(/_/g, " ")}</span>
                  <button
                    type="button"
                    disabled={isSuperAdmin}
                    onClick={() => toggleResource(resource, !allSelected)}
                    className="text-xs text-muted-foreground hover:text-primary disabled:opacity-50"
                  >
                    {allSelected ? "Clear all" : someSelected ? "Select all" : "Select all"}
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                  {items.map((p) => (
                    <label
                      key={p.id}
                      className={cn(
                        "flex cursor-pointer items-start gap-2 border-r border-b px-3 py-2 text-sm last:border-r-0 hover:bg-secondary/40",
                        isSuperAdmin && "cursor-not-allowed opacity-60",
                      )}
                    >
                      <input
                        type="checkbox"
                        className="mt-0.5 h-3.5 w-3.5"
                        checked={isSuperAdmin || selected.has(p.id)}
                        disabled={isSuperAdmin}
                        onChange={() => toggle(p.id)}
                      />
                      <span>
                        <span className="font-medium">{p.action}</span>
                        {p.description && <p className="text-xs text-muted-foreground">{p.description}</p>}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function NewRoleDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (b: boolean) => void }) {
  const qc = useQueryClient();
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const create = useMutation({
    mutationFn: () => api.post("/admin/roles", { slug, name, description }),
    onSuccess: () => {
      toast.success("Role created");
      qc.invalidateQueries({ queryKey: ["roles"] });
      setSlug(""); setName(""); setDescription("");
      onOpenChange(false);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Create failed"),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New role</DialogTitle>
          <DialogDescription>Create a custom role. Then assign permissions in the matrix.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="r-slug">Slug</Label>
            <Input id="r-slug" placeholder="copy_editor" value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))} />
            <p className="text-xs text-muted-foreground">Used internally. Lowercase, underscores only.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="r-name">Display name</Label>
            <Input id="r-name" placeholder="Copy Editor" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="r-desc">Description</Label>
            <Input id="r-desc" placeholder="Edits page copy without publishing" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!slug || !name || create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? "Creating…" : "Create role"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
